<template>
  <v-container>
    <div v-if="evaluationById">

      <v-layout row>
        <v-flex xs8>
          <h1 class="mb-2">
            <span class="font-weight-thin">{{$t('resultsHeader')}}</span>
            {{evaluationById.templateByTemplateId.title}}
          </h1>
          <p class="mb-1 pl-1 subheading">
            {{$d(new Date(evaluationById.startTime), 'long')}}
            <span class="grey--text">
            - 
            {{$d(new Date(evaluationById.endTime), 'long')}}
            </span>
          </p>
          <p class="pl-1 subheading">
            {{evaluationById.userByEvaluatorUserId.fullName}}
          </p>
          <p class="mb-1 pl-1 headline" v-if="evaluationById.teamByTeamId">
            {{evaluationById.teamByTeamId.name}}
          </p>
        </v-flex>
        <v-flex xs1>
          <v-btn
            fab
            color="blue-grey"
            :dark="checkForCommentsOnEvaluation()"
            small
            :flat="!checkForCommentsOnEvaluation()"
            @click="openCommentDialog()"
          >
            <v-icon dark>edit</v-icon>
          </v-btn>
        </v-flex>
        <v-spacer></v-spacer>
        <v-flex xs3>
            <v-btn block color="primary" dark @click="goToEvaluation">{{$t('reopenEvaluation')}}</v-btn>
        </v-flex>
      </v-layout>

      <v-layout row fill-height align-center wrap mb-3>
        <v-flex ma-1 v-for="(node, index) in sortedParticipations" :key="index">
          <v-card>
            <v-card-title primary-title >
              <div>
                <div :style="'color: ' + node.participantRoleByRoleId.color">
                  <v-icon :style="'color: ' + node.participantRoleByRoleId.color">account_box</v-icon>
                  <span class="title">{{node.participantRoleByRoleId.title}}</span>
                </div>
                <div>
                  <span class="subheading">
                    {{node.participantByParticipantId.fullName}}
                  </span>
                </div>
              </div>
            </v-card-title>
          </v-card>
        </v-flex>
      </v-layout>

      <v-card class="mb-3">
        <v-layout row v-if="evaluationScore">
          <v-flex xs12>

            <v-card-title>
            <h1>
              <span class="font-weight-thin">{{$t('overallScore')}}</span>
              <span v-if="evaluationScore.critical.score < evaluationScore.critical.max" class="red--text">{{$t('overallScoreFailed')}}</span>
              <span v-else class="green--text text--darken-2">{{$t('overallScorePassed')}}</span>
            </h1>
            </v-card-title>

            <v-layout row wrap justify-center>
              <v-flex md4 v-if="evaluationScore.critical">
                <v-layout row>
                  <v-flex md7>
                    <h3 class="title text-xs-center font-weight-light mb-2">{{$t('scoreCritical')}}</h3>
                  </v-flex>
                </v-layout>
                <v-layout row>
                  <v-flex md7>
                    <score-gauge size="150"
                      :value="evaluationScore.critical.score / evaluationScore.critical.max * 100"
                      :trendMin="evaluationTrend && evaluationTrend.critical.min"
                      :trendMax="evaluationTrend && evaluationTrend.critical.max"
                      :trendAverage="evaluationTrend && evaluationTrend.critical.average"
                      :color="getCriticalColor(evaluationScore)"/>
                  </v-flex>
                  <v-flex md5 mt-5 v-if="evaluationTrend" class="subheading">
                    <v-layout row class="grey--text text--lighten-1">
                      <v-flex xs6 text-xs-right pr-2> {{$t('min')}} </v-flex> <v-flex xs6> {{evaluationTrend.critical.min}} </v-flex>
                    </v-layout>
                    <v-layout row class="grey--text font-weight-bold">
                      <v-flex xs6 text-xs-right pr-2> {{$t('avg')}} </v-flex> <v-flex xs6> {{evaluationTrend.critical.average}} </v-flex>
                    </v-layout>
                    <v-layout row class="grey--text text--lighten-1">
                      <v-flex xs6 text-xs-right pr-2> {{$t('max')}} </v-flex> <v-flex xs6> {{evaluationTrend.critical.max}} </v-flex>
                    </v-layout>
                  </v-flex>
                </v-layout>
              </v-flex>
              <v-flex md4 offset-md1 v-if="evaluationScore.important">
                <v-layout row>
                  <v-flex md7>
                    <h3 class="title text-xs-center font-weight-light mb-2">{{$t('scoreImportant')}}</h3>
                  </v-flex>
                </v-layout>
                <v-layout row>
                  <v-flex md7>
                    <score-gauge size="150"
                      :value="evaluationScore.important.score / evaluationScore.important.max * 100"
                      :trendMin="evaluationTrend && evaluationTrend.important.min"
                      :trendMax="evaluationTrend && evaluationTrend.important.max"
                      :trendAverage="evaluationTrend && evaluationTrend.important.average"
                      :color="getImportantColor(evaluationScore)"/>
                  </v-flex>
                  <v-flex md5 mt-5 v-if="evaluationTrend" class="subheading">
                    <v-layout row class="grey--text text--lighten-1">
                      <v-flex xs6 text-xs-right pr-2> {{$t('min')}} </v-flex> <v-flex xs6> {{evaluationTrend.important.min}} </v-flex>
                    </v-layout>
                    <v-layout row class="grey--text font-weight-bold">
                      <v-flex xs6 text-xs-right pr-2> {{$t('avg')}} </v-flex> <v-flex xs6> {{evaluationTrend.important.average}} </v-flex>
                    </v-layout>
                    <v-layout row class="grey--text text--lighten-1">
                      <v-flex xs6 text-xs-right pr-2> {{$t('max')}} </v-flex> <v-flex xs6> {{evaluationTrend.important.max}} </v-flex>
                    </v-layout>
                  </v-flex>
                </v-layout>
              </v-flex>
            </v-layout>

          </v-flex>
        </v-layout>
      </v-card>

      <v-layout row v-if="resultPerspectives">
        <v-flex xs12 class="mb-3">
            <div>
              <v-tabs
                v-model="activeTab"
                color="white"
                slider-color="primary"
                class="v-card"
                @change="changePerspective()"
              >
                <v-tab
                  v-for="(perspective, index) in resultPerspectives"
                  :key="index"
                  ripple
                >
                  {{perspective.type}}

                </v-tab>
                <v-tab
                  ripple
                >
                  {{$t('comments')}}

                </v-tab>
                <v-tab-item
                  v-for="tabIndex in 3"
                  :key="tabIndex"
                  transition="slide-y-transition"
                  reverse-transition="slide-y-transition"
                >
                  <!-- spacer to prevent body scrolling to top during transition-->
                  <div v-if="activeTab !== tabIndex-1" style="min-height: 1000px">
                  </div>

                  <div v-if="activeTab === tabIndex-1">
                    <v-expansion-panel 
                      style="box-shadow: none"
                      v-model="expandedPanels"
                      expand
                      v-if="resultPerspectives">
                      <v-layout row white style="padding-left: 60px; padding-right: 60px">
                        <v-flex xs3 offset-xs8>
                          <v-layout row>
                            <v-flex xs3 text-xs-center grey--text>
                              {{$t('scoreCritical')}}
                            </v-flex>
                            <v-flex xs3 offset-xs3 text-xs-center grey--text>
                              {{$t('scoreImportant')}}
                            </v-flex>
                          </v-layout>
                        </v-flex>
                      </v-layout>
                      <v-expansion-panel-content
                        v-for="(categoryResult, i) in resultPerspectives[activeTab].structure"
                        :key="i"
                      >
                        <div slot="header" >
                          <v-layout row wrap pt-2>
                            <v-flex xs8>
                              <!--<v-icon :color="getCategoryResultColor(categoryResult)">fiber_manual_record</v-icon> {{categoryResult.max > 0 ? Math.round(categoryResult.score / categoryResult.max * 100) : 0}}%, -->
                              <span v-if="categoryResult.category.supercategoryIdsList.length >0">{{getSuperCategoryTitleFromSub(categoryResult.category)}}:</span> 
                              {{categoryResult.category.title}}
                              <v-tooltip bottom 
                                max-width="75%"
                                v-if="categoryResult.category.info && !expandedPanels[i]">
                                <v-icon 
                                  slot="activator" 
                                  class="ml-2" style="vertical-align: middle" color="blue-grey lighten-3">info</v-icon>
                                <vue-markdown class="info-tooltip category-info-tooltip">{{categoryResult.category.info}}</vue-markdown>
                              </v-tooltip>

                            </v-flex>
                            <v-flex xs3>
                              <v-layout row>
                                <v-flex xs3 style="margin-bottom: -20px; margin-top: -10px">
                                    <score-gauge size="50" v-if="!categoryResult.critical.zooz"
                                      :value="categoryResult.critical.max > 0 ? categoryResult.critical.score / categoryResult.critical.max * 100 : 0"
                                      :trendMin="categoryResult.trend && categoryResult.trend.critical.min"
                                      :trendMax="categoryResult.trend && categoryResult.trend.critical.max"
                                      :trendAverage="categoryResult.trend && categoryResult.trend.critical.average"
                                      :color="getCriticalColor(categoryResult)"/>
                                    <div v-if="categoryResult.critical.isNa" class="blue-grey--text text-xs-center pt-3">
                                      N/A
                                    </div>
                                </v-flex>
                                <v-flex xs3 style="margin-top: -5px; margin-bottom: -5px">
                                  <div v-if="!categoryResult.critical.zooz && categoryResult.trend" class="caption text-xs-center" style="line-height: 13px">
                                    <div class="grey--text text--lighten-1">{{categoryResult.trend.critical.min}}</div>
                                    <div class="grey--text font-weight-bold">{{categoryResult.trend.critical.average}}</div>
                                    <div class="grey--text text--lighten-1">{{categoryResult.trend.critical.max}}</div>
                                  </div>
                                </v-flex>
                                <v-flex xs3 style="margin-bottom: -20px; margin-top: -10px">
                                    <score-gauge size="50" v-if="!categoryResult.important.zooz"
                                      :value="categoryResult.important.max > 0 ? categoryResult.important.score / categoryResult.important.max * 100 : 0"
                                      :trendMin="categoryResult.trend && categoryResult.trend.important.min"
                                      :trendMax="categoryResult.trend && categoryResult.trend.important.max"
                                      :trendAverage="categoryResult.trend && categoryResult.trend.important.average"
                                      :color="getImportantColor(categoryResult)"/>
                                    <div v-if="categoryResult.important.isNa" class="blue-grey--text text-xs-center pt-3">
                                      N/A
                                    </div>
                                </v-flex>
                                <v-flex xs3 style="margin-top: -5px; margin-bottom: -5px">
                                  <div v-if="!categoryResult.important.zooz && categoryResult.trend" class="caption text-xs-center" style="line-height: 13px">
                                    <div class="grey--text text--lighten-1">{{categoryResult.trend.important.min}}</div>
                                    <div class="grey--text font-weight-bold">{{categoryResult.trend.important.average}}</div>
                                    <div class="grey--text text--lighten-1">{{categoryResult.trend.important.max}}</div>
                                  </div>
                                </v-flex>
                              </v-layout>
                            </v-flex>
                            <v-flex xs1 v-on:click.stop text-xs-right v-if="tabIndex === 1">
                              <v-btn
                                fab
                                color="blue-grey lighten-2"
                                :dark="checkForCommentsOnCategory(categoryResult.category.id)"
                                small
                                class="category-comment-button"
                                :flat="!checkForCommentsOnCategory(categoryResult.category.id)"
                                @click="openCommentDialog(null, categoryResult.category)"
                              >
                                <v-icon dark>edit</v-icon>
                              </v-btn>
                            </v-flex>
                          </v-layout>
                        </div>
                        <v-card>
                          <v-container>
                            <v-layout row wrap mb-3
                              v-if="categoryResult.category.info"
                              class="grey--text">
                              <v-flex xs11>
                                <vue-markdown class="info-text category-info-text">{{categoryResult.category.info}}</vue-markdown>
                              </v-flex>
                            </v-layout>
                            <div v-for="(indicator, indicatorIndex) in categoryResult.indicators" :key="indicator.id" class="pt-1"
                              @click="clickIndicator(indicator)">
                              <div v-if="expandedPanels[i]">
                                <div v-if="indicator.teamBased">
                                  <v-layout row wrap class="indicator-row teambased" :class="indicator.isNa ? 'na' : ''">
                                    <v-flex xs1 text-xs-center>
                                      {{indicator.score}}
                                    </v-flex>
                                    <v-flex xs7>
                                      <span class="body-1">{{indicator.title}}</span>
                                      <span v-for="role in indicator.roles" :key="role.id" :style="indicatorRoleStyle(indicator, role)">
                                        {{role.title}}
                                      </span>
                                      <v-tooltip bottom
                                        max-width="75%"
                                        v-if="indicator.info && !showInfo[indicator.id]">
                                        <v-icon 
                                          slot="activator"
                                          class="ml-2" style="vertical-align: middle" color="blue-grey lighten-3">info</v-icon>
                                        <vue-markdown class="info-tooltip indicator-info-tooltip">{{indicator.info}}</vue-markdown>
                                      </v-tooltip>
                                    </v-flex>
                                    <v-flex xs3 text-xs-center>
                                      <span v-if="indicator.isNa" class="na">
                                        N/A
                                      </span><span v-else>
                                        <v-icon v-if="indicator.observed">check_box</v-icon>
                                        <v-icon v-else>check_box_outline_blank</v-icon>
                                      </span>
                                    </v-flex>
                                    <v-flex xs1 v-on:click.stop>
                                      <v-btn
                                        fab
                                        color="blue-grey lighten-2"
                                        :dark="checkForCommentsOnIndicator(indicator.id)"
                                        small
                                        class="category-comment-button"
                                        :flat="!checkForCommentsOnIndicator(indicator.id)"
                                        @click="openCommentDialog(indicator)"
                                      >
                                        <v-icon dark>edit</v-icon>
                                      </v-btn>
                                    </v-flex>
                                  </v-layout>
                                </div><div v-else>
                                  <v-layout row wrap class="indicator-row" :class="indicator.isNa ? 'na' : ''">
                                    <v-flex xs1 text-xs-center>
                                      {{indicator.score}}
                                    </v-flex>
                                    <v-flex xs7>
                                      <span class="body-1">{{indicator.title}}</span>
                                      <v-tooltip bottom
                                        max-width="75%"
                                        v-if="indicator.info && !showInfo[indicator.id]">
                                        <v-icon 
                                          slot="activator"
                                          class="ml-2" style="vertical-align: middle" color="blue-grey lighten-3">info</v-icon>
                                        <vue-markdown class="info-tooltip indicator-info-tooltip">{{indicator.info}}</vue-markdown>
                                      </v-tooltip>
                                    </v-flex>
                                    
                                    <v-flex xs3 text-xs-center>
                                      <span v-for="role in indicator.roles" :key="role.id">
                                        <span class="text-xs-left"> 
                                          <span v-if="indicator.isNaPerRole[role.id]">
                                            <div class="role role-na">
                                              {{role.title}}
                                            </div>
                                          </span>
                                          <span v-else>
                                            <div class="role" :style="indicatorRoleStyle(indicator, role)">
                                              {{role.title}}
                                            </div>
                                          </span>
                                        </span>
                                      </span>
                                    </v-flex>
                                    <v-flex xs1 v-on:click.stop>
                                      <v-btn
                                        fab
                                        color="blue-grey lighten-2"
                                        :dark="checkForCommentsOnIndicator(indicator.id)"
                                        small
                                        class="category-comment-button"
                                        :flat="!checkForCommentsOnIndicator(indicator.id)"
                                        @click="openCommentDialog(indicator)"
                                      >
                                        <v-icon dark>edit</v-icon>
                                      </v-btn>
                                    </v-flex>
                                  </v-layout>
                                </div>
                                <v-slide-y-transition>
                                  <v-layout row wrap mt-3 
                                    v-if="showInfo[indicator.id]" class="grey--text">
                                    <v-flex xs9 offset-xs1>
                                      <vue-markdown class="info-text indicator-info-text">{{indicator.info}}</vue-markdown>
                                    </v-flex>
                                  </v-layout>
                                </v-slide-y-transition>
                              </div><div v-else>
                                <div style="height: 40px"></div> <!-- spacer for rendering transition -->
                              </div>
                              <v-divider v-if="indicatorIndex != categoryResult.indicators.length -1" class="mt-2"></v-divider>
                            </div>
                          </v-container>
                        </v-card>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </div>
                </v-tab-item>

                <v-tab-item key="4"
                  v-if="activeTab == 3"
                  transition="slide-y-transition"
                  reverse-transition="slide-y-transition"
                >
                  <comments-summary 
                    ref="commentsSummary" 
                    :commentsInEvaluation="evaluationById.commentsByEvaluationId.nodes"
                    :evaluationId="evaluationId"
                    :currentUser="currentUser"
                    v-on:commentEvent="updateComments"
                    />
                </v-tab-item>
              </v-tabs>
            </div>
        </v-flex>
      </v-layout>
      <comment-dialog 
            ref="commentDialog"
            :allComments="allComments"
            :evaluationId="evaluationId"
            :currentUser="currentUser"
            v-on:commentEvent="updateComments"
            />
    </div>
  </v-container>
</template>

<style scoped>
  p.title {
    text-align: center;
  }
  div.role {
    display: inline-block;
    border: 1px solid;
    padding: 0 3px 0 3px;
    border-radius: 3px;
    text-align: center;
    margin-left: 2px;
  }
  .role-observed {
    font-weight: bold;
  }
  div.role-na {
    color: #aaa;
    background-color: #f5f5f5;
    border-color: #ccc;
  }
  .indicator-row.na {
    color: #888;
  }
  .category-comment-button {
    max-height:30px;
    max-width:30px;
    margin-top: 0px;
  }
  .info-tooltip {
    font-size: 14px;
    padding: 10px;
  }
</style>

<script>
  import { evaluationByIdQuery, categoriesQuery, evaluationTrendQuery } from '@/graphql/evaluationQueries.gql'
  import { allParticipantsQuery, allParticipantRolesQuery } from '@/graphql/participantQueries.gql'
  import { commentsByEvaluationIdQuery } from '@/graphql/commentQueries.gql'
  import ScoreCalculator from '@/scorecalculator'
  import ScoreGauge from '@/components/ScoreGauge.vue'
  import CommentsSummary from '@/components/CommentsSummary.vue'
  import CommentDialog from '@/components/CommentDialog.vue'
  import VueMarkdown from 'vue-markdown'

  //import gql from 'graphql-tag'
  export default {
    /*apollo: {
      templateById: templateByIdQuery(this.templateId)
    },*/
    components: {
      ScoreGauge,
      CommentsSummary,
      CommentDialog,
      VueMarkdown
    },
    apollo: {
      evaluationById:{
        query: evaluationByIdQuery,
        // Reactive parameters
        variables() {
          // Use vue reactive properties here
          return {
              evaluationId: this.evaluationId,
          }
        },
      },
      finishedEvaluationsByTemplateId: {
        query: evaluationTrendQuery,
        variables() {
          return {
            templateId: this.evaluationById.templateByTemplateId.id
          }
        },
        skip() {
          return !this.evaluationById
        }
      },
      allComments: {
      query: commentsByEvaluationIdQuery,
        variables() {
          return {
            evaluationId: this.evaluationId
          };
        }
      },
      allParticipants: allParticipantsQuery,
      allParticipantRoles: allParticipantRolesQuery,
      allCategories: categoriesQuery,
      
    },
    methods: {
      changePerspective() {
        this.expandedPanels = []
        this.showInfo = {}
      },
      clickIndicator(indicator) {
        if (indicator.info) {
          this.$set(this.showInfo, indicator.id, !this.showInfo[indicator.id])
        }
      },
      updateComments(){
        this.$apollo.queries.allComments.refetch()
        this.$apollo.queries.evaluationById.refetch()
      },
      openCommentDialog(indicator = null, category = null, participant = null) {
        this.$refs.commentDialog.openDialog(indicator, category, participant)
      },
      checkIndicatorStatus (indicatorId, participantId){
        for(var index in this.$apollo.data.allObservations.nodes){
          if ( this.$apollo.data.allObservations.nodes[index].indicatorId == indicatorId && this.$apollo.data.allObservations.nodes[index].participantId == participantId){
            return true
          }
        }
        return false
      },
      getCategoryResultColor(categoryResult) {
        if(categoryResult.zooz){
          return "grey"
        } else if(categoryResult.critical.hasMissing){
          return "error"
        } else if(categoryResult.score / categoryResult.max >= 0.99){
          return "green"
        } else {
          return "orange"
        }
      },
      getCriticalColor(result) {
        if (result.critical.zooz) {
          return "grey"
        } else if(result.critical.score / result.critical.max >= 0.99){
          return "green"
        } else {
          return "red"
        }
      },
      getImportantColor(result) {
        if (result.important.zooz) {
          return "grey"
        } else if(result.important.score / result.important.max >= 0.99){
          return "green"
        } else {
          return "orange"
        }
      },
      indicatorRoleStyle(indicator, role) {
        if (indicator.observedPerRole && indicator.observedPerRole[role.id]) {
          return {
            'color': 'white',
            'background-color': role.color,
            'border-color': role.color
          }
        } else {
          return {
            'color': role.color,
            'border-color': role.color,
          }
        }
      },
      getSuperCategoryTitleFromSub(category){
        var cat = null
        if(this.allCategories && category.supercategoryIdsList.length > 0){
          cat = this.allCategories.nodes.find(superCategory => {
            return superCategory.id === category.supercategoryIdsList[0]
          })
          if(cat){
            cat = cat.title
          }
        }
        return cat
      },
      
      goToEvaluation(){
        this.$router.push({
          name: "evaluation",
          params: { evaluationId: this.evaluationId }
        }); //params: ...
      },
      
 
      checkForCommentUpdate(originalComment, currentContent = null){
        if(originalComment){
          if(originalComment.content === currentContent){
            return true
          }
        }
        return false
      },
      
      checkForCommentsOnEvaluation(){
        if(this.evaluationById.commentsByEvaluationId.nodes){
          for (var i in this.evaluationById.commentsByEvaluationId.nodes) {
            if(!this.evaluationById.commentsByEvaluationId.nodes[i].participantByParticipantId && 
            !this.evaluationById.commentsByEvaluationId.nodes[i].categoryByCategoryId &&
            !this.evaluationById.commentsByEvaluationId.nodes[i].indicatorByIndicatorId){
              return true
            }
          }
        }
        return false
      },
      getCommentsOnEvaluation(){
        let comments = []
        if(this.evaluationById.commentsByEvaluationId.nodes){
          for (let node of this.evaluationById.commentsByEvaluationId.nodes) {
            if(!node.participantByParticipantId && 
            !node.categoryByCategoryId &&
            !node.indicatorByIndicatorId){
              comments.push(node)
            }
          }
        }
        return comments
      },
      checkForCommentsOnIndicator(indicatorId, participantId = null) {
        if(this.evaluationById.commentsByEvaluationId.nodes){
          for (let node of this.evaluationById.commentsByEvaluationId.nodes) {  
            if (node.indicatorByIndicatorId &&
              node.indicatorByIndicatorId.id === indicatorId
            ) {
              if(participantId){
                if(node.participantByParticipantId && node.participantByParticipantId.id === participantId){
                  return true
                } else {
                  return false
                }
              } 
              return true;
            }
          }
        }
        return false;
      },
      getCommentsOnIndicator(indicatorId, participantId = null) {
        var comments = [];
        if(this.evaluationById){
          for (let node of this.evaluationById.commentsByEvaluationId.nodes) { 
            if (node.indicatorByIndicatorId && node.indicatorByIndicatorId.id === indicatorId) {
              if( !participantId ){
                comments.push(node);
              } else if( participantId && node.participantByParticipantId && node.participantByParticipantId.id === participantId){
                comments.push(node);
              }
            }
          }
        }
        return comments;
      },
      checkForCommentsOnCategory(categoryId) {
        if(this.evaluationById){
          for (let node of this.evaluationById.commentsByEvaluationId.nodes) { 
            if (
              categoryId && 
              node.categoryByCategoryId &&
              node.categoryByCategoryId.id === categoryId
            ) {
              return true;
            }
          }
        }
        return false;
      },
      getCommentsOnCategory(categoryId) {
        var comments = [];
        if(this.evaluationById){
          for (let node of this.evaluationById.commentsByEvaluationId.nodes) { 
            if (node.categoryByCategoryId && node.categoryByCategoryId.id === categoryId) {
              comments.push(node);
            }
          }
        }
        return comments;
      },
      guid() {
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
          var r = (Math.random() * 16) | 0, v = c == "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
      },
      updateScore() {
        if (!this.evaluationById) return;
        const calculator = new ScoreCalculator(this.allCategories, this.allParticipantRoles);
        this.evaluationScore = calculator.calculateScore(this.evaluationById);
        if (this.finishedEvaluationsByTemplateId) {
          this.evaluationTrend = calculator.calculateTrend(this.finishedEvaluationsByTemplateId.nodes, this.evaluationById.templateByTemplateId)
        }
        this.resultPerspectives = this.createResultPerspectives(this.evaluationScore, this.evaluationTrend);
      },
      createResultPerspectives(evaluationScore, evaluationTrend) {
          const categories = []
          // Sum up by category as an array of categories, their scores and indicators
          for (const categoryId in evaluationScore.byCategory) {
              const category = this.allCategories && this.allCategories.nodes.find(cat => cat.id === categoryId)
              const cat = {
                  category,
                  ...evaluationScore.byCategory[categoryId]
              }
              if (evaluationTrend && evaluationTrend.byCategory[categoryId]) {
                  cat.trend = evaluationTrend.byCategory[categoryId]
              }
              categories.push(cat)
          }

          // Present results in "perspectives", evaluation score by a selection of categories.
          var resultPerspectives = []
          var eventStructure = []
          var operatorFundamentalsStructure = []
          var knowledgeStructure = []

          for (var result of categories) {
              if (!result.category) continue;
              if (result.category.type === "operator_fundamentals") {
                  operatorFundamentalsStructure.push(result)
              } else if (result.category.type === "event_detail" || result.category.title === "Generella kriterier" || result.category.title === "Processförståelse") {
                  eventStructure.push(result)
              } else if (result.category.type === "knowledge_domain") {
                  knowledgeStructure.push(result)
              }
          }

          eventStructure.sort((a, b) => a.category.sortWeight - b.category.sortWeight)
          for (var event of eventStructure) {
              event.indicators.sort((a, b) => {
                  var a_indcat = a.indicatorCategoriesByIndicatorIdList.find(indcat => indcat.categoryId === event.category.id)
                  var b_indcat = b.indicatorCategoriesByIndicatorIdList.find(indcat => indcat.categoryId === event.category.id)
                  return (a_indcat ? a_indcat.sortWeight : 0) - (b_indcat ? b_indcat.sortWeight : 0)
              })
          }
          resultPerspectives.push({ 'type': this.$t('catEvent'), 'structure': eventStructure })
          resultPerspectives.push({ 'type': this.$t('catOperatorFundamentals'), 'structure': operatorFundamentalsStructure })
          resultPerspectives.push({ 'type': this.$t('catKnowledge'), 'structure': knowledgeStructure })
          return resultPerspectives
      }
    },
    mounted() {
      if(!this.evaluationId){
        this.$router.push({ name: 'home'})
      }
      this.$apollo.queries.evaluationById.refetch()
    },
    computed: {
      currentUser() { return this.$store.state.currentUser },
      sortedParticipations(){
        if (!this.evaluationById) return []
        const participations = this.evaluationById.participationsByEvaluationId.nodes
        return participations.concat().sort((a,b) => a.participantRoleByRoleId.sortWeight - b.participantRoleByRoleId.sortWeight)
      },
    },
    data() { return {
      expandedPanels: [],
      evaluationScore: null,
      evaluationTrend: null,
      resultPerspectives: null,
      started: false,
      templateById: null,
      allParticipants: null,
      allParticipantRoles: null,
      selectedParticipant: null,
      selectedParticipantRole: null,
      evaluationById: null,
      observations: null,
      localObservations: null,
      activeTab: null,
      allCategories: null,
      dialog:false,
      commentTextInput: null,
      mutatingComment: null,
      selectedParticipants: [{id:1}],
      showInfo: {},
    }},
    watch: {
      evaluationById() { this.updateScore() },
      allCategories() { this.updateScore() },
      finishedEvaluationsByTemplateId() { this.updateScore() },
    },
    props: ['evaluationId'],
    
  }
</script>